import { downloadFile } from 'further-ui/utils';
import { useDownload } from 'hooks/ui/useDownload';
import api, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useState } from 'react';
import resourceExists from 'utils/resourceExists';

// This hook will try and download a PDF update for a specific investor & exitstatement ID.
// 1. If includePreviousExitStatements is true, it will always use the export endpoint (which merges PDFs)
// 2. Otherwise, it will see if the file exists in S3 at the provided pdfUrl using `resourceExists()`
// 3. If it does exist in S3, it will download it from there
// 4. If it doesn't exist in S3, it will call `exit-statement/export-pdf-by-investor/...` which generates it on demand

export function useDownloadPdfExitStatement(params) {
  const {
    exitStatementId,
    investorId,
    filename,
    pdfUrl,
    includePreviousExitStatements = false,
  } = params;

  const exportPdf = api.exitStatement.exportPdf.useMutation(
    useHandleTrpcMutation<ApiResponses['exitStatement']['exportPdf']>()({
      onSuccess: async (data) => {
        const binaryString = atob(data.file);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });

        downloadFile(blob, data.filename || filename);
      },
    }),
  );

  const download = useDownload({
    filename,
    errorMessage: 'Exit Statement PDF could not be downloaded.',
  });
  const [loading, setLoading] = useState(false);

  const processDownload = async () => {
    if (!loading) {
      setLoading(true);

      // If includePreviousExitStatements is true, always use the export endpoint
      // which will merge previous PDFs. Don't download directly from S3.
      if (includePreviousExitStatements) {
        await exportPdf.mutateAsync({
          exitStatementId,
          investorId,
        });
      } else {
        // the signed url given above is only valid for getObject requests, hence a HEAD request
        // won't work. A more performant future solution would be to have an endpoint that returns
        // signed headObject urls OR ensure the get is properly cached for the subsequent download
        if (await resourceExists(pdfUrl, true)) {
          // file exists in s3 - take it from there
          download.mutate(pdfUrl);
        } else {
          // file is not in s3, perhaps it's still being generated or it was
          // never generated, so generate it on demand
          await exportPdf.mutateAsync({
            exitStatementId,
            investorId,
          });
        }
      }
      setLoading(false);
    }
  };

  return { download: processDownload, loading: loading || download.isPending };
}
